import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import { api } from '../services/api';

type Step = 'email' | 'otp';

export function ExternalLoginPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setError('Please enter your email');
      return;
    }

    setSubmitting(true);
    setError(null);
    setInfo(null);

    try {
      await api.externalRequestOtp(normalized);
      setEmail(normalized);
      setStep('otp');
      setInfo('We sent a verification code to your email. It expires in 15 minutes.');
    } catch (err: any) {
      setError(err?.message || 'Failed to send verification code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.trim();
    if (!code) {
      setError('Please enter the verification code');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await api.externalVerifyOtp(email, code);
      window.location.replace(result.redirect || '/super-picker');
    } catch (err: any) {
      setError(err?.message || 'Invalid or expired code. Please try again.');
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      await api.externalRequestOtp(email);
      setInfo('A new verification code has been sent to your email.');
    } catch (err: any) {
      setError(err?.message || 'Failed to resend code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          mx: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600} gutterBottom textAlign="center">
          Super Picker
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
          {step === 'email'
            ? 'Sign in with your invited email'
            : `Enter the 6-digit code sent to ${email}`}
        </Typography>

        {step === 'email' ? (
          <form onSubmit={handleRequestOtp}>
            <TextField
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              fullWidth
              autoFocus
              disabled={submitting}
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || !email.trim()}
              size="large"
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Send code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp}>
            <TextField
              label="Verification code"
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                if (error) setError(null);
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              fullWidth
              autoFocus
              disabled={submitting}
              sx={{ mb: 2 }}
            />

            {info && !error && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {info}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || otp.trim().length < 6}
              size="large"
              sx={{ mb: 1.5 }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Verify & sign in'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                  setError(null);
                  setInfo(null);
                }}
                disabled={submitting}
                underline="hover"
              >
                Use a different email
              </Link>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleResend}
                disabled={submitting}
                underline="hover"
              >
                Resend code
              </Link>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
}

export default ExternalLoginPage;
